import { useState, type SyntheticEvent } from "react";
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Textarea,
  useToast,
  Link,
  Divider,
  AbsoluteCenter,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import contactBgImg from "./lone_rider_contact_bg.webp";

const ContactForm = () => {
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isSavingForm, setIsSavingForm] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e: SyntheticEvent<EventTarget>) => {
    e.preventDefault();

    if (!email || !title || !message) {
      toast({
        title: "Błąd",
        description: "Wszystkie pola są wymagane.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (title.length > 500) {
      toast({
        title: "Błąd",
        description: "Tytuł nie może być dłuższy niż 500 znaków.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (message.length > 3000) {
      toast({
        title: "Błąd",
        description: "Wiadomość nie może zawierać więcej niż 3000 znaków.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSavingForm(true);
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, title, message }),
      });

      if (!response.ok) {
        throw new Error("Błąd przetwarzania wiadomości");
      }

      toast({
        title: "Wiadomość wysłana",
        description: "Twoja wiadomość została wysłana pomyślnie.",
        status: "success",
        position: "bottom-left",
        duration: 5000,
        isClosable: true,
      });

      setEmail("");
      setTitle("");
      setMessage("");
    } catch (error) {
      toast({
        title: "Błąd",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        description: error.message,
        status: "error",
        duration: 5000,
        position: "bottom-left",
        isClosable: true,
      });
    } finally {
      setIsSavingForm(false);
    }
  };

  return (
    <Stack
      borderRadius={4}
      direction={{ base: "column-reverse", md: "row" }}
      bgImage={{ base: "none", md: contactBgImg }}
      bgSize={{ base: "100%", md: "cover" }}
      bgRepeat={"no-repeat"}
      bgClip={"border-box"}
      bgPosition={{ base: "50% 70%", md: "10% 50%" }}
      mr={{ base: 0, md: "200" }}
      py={{ base: 0, md: 16 }}
      justifyContent={"right"}
    >
      <Box
        bgColor={"white"}
        borderRadius={4}
        position={"relative"}
        right={{ base: 0, md: "-200" }}
        top={0}
        p={4}
        my={{ base: 0, md: 8 }}
        as="form"
        boxShadow={"lg"}
        onSubmit={(e: SyntheticEvent<EventTarget>): void => {
          handleSubmit(e);
        }}
        width={{ base: "100%", md: "md" }}
      >
        <Link
          as={RouterLink}
          p={2}
          color="primary.500"
          fontSize={{ base: "lg" }}
          href="tel:0048505541948"
        >
          +48505541948
        </Link>
        <br />
        <Link
          as={RouterLink}
          p={2}
          color="primary.500"
          fontSize={{ base: "lg" }}
          href="mailto:kontakt@dystans.cc"
        >
          kontakt@dystans.cc
        </Link>
        <Box position="relative" padding="10">
          <Divider />
          <AbsoluteCenter bg="white" px="4">
            lub formularzem
          </AbsoluteCenter>
        </Box>
        <FormControl id="email" isRequired>
          <FormLabel>E-mail</FormLabel>
          <Input
            type="email"
            value={email}
            variant={"outline"}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormControl>
        <FormControl id="title" isRequired mt={4}>
          <FormLabel>Tytuł</FormLabel>
          <Input
            type="text"
            variant={"outline"}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
            minLength={3}
          />
        </FormControl>
        <FormControl id="message" isRequired mt={4}>
          <FormLabel>Wiadomość</FormLabel>
          <Textarea
            value={message}
            minHeight={100}
            variant={"outline"}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={3000}
            minLength={3}
          />
        </FormControl>
        <Box mt={4} alignItems={"center"}>
          <Center>
            <Button type="submit" isLoading={isSavingForm}>
              Wyślij wiadomość
            </Button>
          </Center>
        </Box>
      </Box>
    </Stack>
  );
};

export default ContactForm;
